<template>
  <div class="bottom-panel">
    <Tabs :default-active-key="tabPaneKey.VIDEO">
      <TabPane :key="tabPaneKey.VIDEO" :tab="$t('home.VideoRecord')">
        <div class="video-content">
          <div class="video-section">
            <VideoPlayer v-if="videoUrl" :src="videoUrl" />
            <CocAssetsEmpty srcType="video" v-else />
          </div>
          <template v-if="eventCalls.length > 0">
            <div class="video-split-footer">
              <div
                class="video-content-toggle show-toggle"
                @click="videoSplitVisible = true"
              >
                <span class="toggle-arrow"><SvgIcon icon-class="arrow"/></span>{{$t('home.VideoList')}}
              </div>
            </div>
            <div
              class="video-split-content"
              :class="{ 'video-split-open': videoSplitVisible }"
            >
              <FlexScrollBox>
                <div class="video-split-header" slot="header"></div>
                <VideoList
                  slot="main"
                  :items="eventCalls"
                  @select="handleEventCallSelect"
                />
                <div class="video-split-footer" slot="footer">
                  <div
                    class="video-content-toggle hide-toggle"
                    @click="videoSplitVisible = false"
                  >
                    {{$t('home.Back')}}<span class="toggle-arrow"
                      ><SvgIcon icon-class="arrow"
                    /></span>
                  </div>
                </div>
              </FlexScrollBox>
            </div>
          </template>
        </div>
      </TabPane>
      <TabPane :key="tabPaneKey.FILE" :tab="$t('home.Files')">
        <div style="padding: 32px 0;min-height: 412px;">
          <div style="position: relative"  v-if="existsFiles">
                <div style="padding: 0 32px">
                  <Button class="upload-btn" type="default" shape="round" @click="handleUploadAttachFile">
                    <SvgIcon icon-class="upload" class-name="icon-upload" />
                    <span class="upload-text">{{$t('home.UploadAttachment')}}</span>
                  </Button>
                </div>
                <Table class="coc-attach-table" :columns="attachFileColumns" :scroll="{x: 874}" :data-source="currentCoc.files" :bordered="false" :pagination="false" rowKey="fid">
                  <template slot="fileName" slot-scope="text">
                    <div class="attach-name-td"><FileEntityIcon :filename="text"/>{{getAttachName(text)}}</div>
                  </template>
                  <template slot="action" slot-scope="record">
                    <div class="coc-attach-table-action">
                      <Tooltip :title="$t('common.Download')" placement="bottom">
                        <SvgIcon
                          class-name="download-icon"
                          icon-class="download"
                          @click.native="handleDownloadFile(record)"
                        />
                      </Tooltip>
                      <template v-if="hasDeleteAttachPriv(record)">
                        <Tooltip :title="$t('common.Delete')" placement="bottom">
                          <span class="remove-btn" @click="handleDeleteAttachFile(record.fid)"></span>
                        </Tooltip>
                      </template>
                    </div>
                  </template>
                </Table>
            </div>
          <CocAssetsEmpty srcType="file" v-else>
            <div style="padding: 0 32px">
              <Button class="upload-btn" type="default" shape="round" @click="handleUploadAttachFile">
                <SvgIcon icon-class="upload" class-name="icon-upload" />
                <span class="upload-text">{{$t('home.UploadAttachment')}}</span>
              </Button>
            </div>
          </CocAssetsEmpty>
        </div>
      </TabPane>
    </Tabs>
  </div>
</template>
<script>
import { Tabs, Table, Tooltip, Button } from 'ant-design-vue'
import VideoPlayer from '@/components/behavior/video-player/index.vue'
import CocAssetsEmpty from '@/components/business/empty/CocAssetsEmpty.vue'

import VideoList from './VideoList.vue'
import SvgIcon from '@/components/behavior/svg-icon/index.vue'
import FlexScrollBox from '@/components/behavior/flex-scroll-box/index.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { getSplitTime } from '@/utils/format'
import { format } from 'date-fns'

import emptyContent from '@/assets/empty-content.svg'
import { fileSelector } from '@/utils/file-selector'
import { CONFERENCE_STATE } from '@/utils/constant'
import { FileEntityIcon } from '@/components/behavior/file-entiry/index'
import { download } from '@/utils/download'
import { uploadFile } from '@/utils/upload'

const TAB_PANE_KEY = Object.freeze({
  VIDEO: 'VIDEO',
  FILE: 'FILE'
})

export default {
  name: 'CocRecord',
  components: {
    Tabs,
    TabPane: Tabs.TabPane,
    VideoPlayer,
    VideoList,
    SvgIcon,
    FlexScrollBox,
    CocAssetsEmpty,
    Table,
    Tooltip,
    Button,
    FileEntityIcon
  },
  props: {
    channelId: {
      type: String,
      default: null
    }
  },
  data() {
    const _this = this
    return {
      videoSplitVisible: false,
      videoUrl: '',
      attachFileColumns: [
        { title: '', key: 'firstCell', width: 56 },
        { get title() { return _this.$t('home.File') }, dataIndex: 'name', width: '46%', scopedSlots: { customRender: 'fileName' } },
        { get title() { return _this.$t('home.From') }, dataIndex: 'userId', width: '21%', customRender: (text) => this.currentCoc.memberByUid[text].nickName },
        { get title() { return _this.$t('home.Time') }, dataIndex: 'addTime', width: '25%', customRender: (text) => format(text * 1000, 'yyyy-MM-dd HH:mm:ss') },
        { title: '', key: 'action', width: 100, scopedSlots: { customRender: 'action' } }
      ]
    }
  },
  computed: {
    ...mapState(['userInfo']),
    ...mapState('coc', ['currentCocId']),
    ...mapGetters('coc', ['getRecordByChannelId', 'currentCoc']),
    eventCalls() {
      if (!this.channelId) {
        return []
      }
      const record = this.getRecordByChannelId(this.channelId)

      const eventCalls = []
      if (record !== null) {
        const createdTime = record.createdTime // 秒

        if (record.videos?.length > 0) {
          eventCalls.push(
            this.calculatedEventCall(null, this.$t('home.CompositeVideo'), createdTime, record.videos)
          )
        }

        if (record.records?.length > 0) {
          record.records.forEach(record => {
            if (record.videos?.length > 0) {
              eventCalls.push(
                this.calculatedEventCall(
                  record.uid,
                  record.name,
                  createdTime,
                  record.videos
                )
              )
            }
          })
        }
      }
      return eventCalls
    },
    tabPaneKey() {
      return TAB_PANE_KEY
    },
    emptyContent() {
      return emptyContent
    },
    existsFiles() {
      const files = this.currentCoc.files
      return (files && files.length > 0) || false
    }
  },

  methods: {
    ...mapActions('coc', ['deleteAttachFile', 'addAttachFiles']),
    handleEventCallSelect(part) {
      this.videoUrl = part.url
    },
    calculatedEventCall(userId, name, createdTime, videos) {
      return {
        userId,
        name,
        parts: videos.map(video => {
          const beginTime = getSplitTime(video.startTime - createdTime)
          const endTime = getSplitTime(video.endTime - createdTime)
          return {
            url: video.videoURL,
            rangeFormat: `${beginTime}~${endTime}`
          }
        })
      }
    },
    hasDeleteAttachPriv(record) {
      const { hostIsMe } = this.currentCoc
      return hostIsMe || this.userInfo.id === record.userId
    },
    async handleDeleteAttachFile(fid) {
      const conferenceId = this.currentCocId
      await this.deleteAttachFile({
        conferenceId,
        files: [{ fid }]
      })
    },
    handleUploadAttachFile() {
      fileSelector(async (files) => {
        console.error(files, files.length)
        if (files.length === 1) {
          const file = files.item(0)
          if (file.size > 50 * 1024 * 1024) {
            this.$confirm({
              content: this.$t('home.More50MFile'),
              width: '460px',
              centered: true,
              class: 'confirm-exit',
              closable: true,
              cancelText: this.$t('common.Cancel'),
              okText: this.$t('common.Ok'),
            })
          } else {
            const formData = new FormData()
            formData.append('file', file)

            const fileName = file.name

            const conferenceId = this.currentCocId

            if (conferenceId) {
              const cocDetail = this.currentCoc

              if (cocDetail && cocDetail.confererceState === CONFERENCE_STATE.IN_PROGRESS && cocDetail.roomId) {
                formData.append('channelId',)
              }
            }
            const url = await uploadFile(formData)
            this.addAttachFiles({
              conferenceId,
              files: [{
                url,
                name: fileName,
                type: 0
              }],
              notify: true
            })
          }
        }
      })
    },
    getAttachName(text) {
      const lastDotIndex = text.lastIndexOf('.')
      if (lastDotIndex > -1) {
        return text.substring(0, lastDotIndex)
      }
      return text
    },
    handleDownloadFile(file) {
      download(file.url, file.name)
    }
  },
  watch: {
    channelId() {
      this.videoUrl = ''
    },
  }
}
</script>
<style lang="less" scoped>
@import url(~@/styles/font.less);
@import url(~@/styles/button.less);

.bottom-panel {
  background: #ffffff;
  box-shadow: 0px 5px 30px 0px rgba(89, 132, 219, 0.08);
  border-radius: 16px;
  overflow: hidden;
  ::v-deep .ant-tabs-bar {
    margin-bottom: 0;
  }

  ::v-deep .ant-tabs-tab {
    .regular-font(16);
    color: #666666;
    margin: 0 0 0 56px;
    padding: 24px 0 12px 0;

    &.ant-tabs-tab-active  {
      .semibold-font(16);
      color: #236cff;
    }

    &:first-child {
      margin-left: 32px;
    }

  }

  ::v-deep .ant-tabs-ink-bar {
    background: transparent;
    &::after {
      display: block;
      content: ' ';
      height: 100%;
      background-color: #236cff;
      width: 30px;
      margin-left: 50%;
      transform: translateX(-50%);
    }
  }

  .video-content {
    display: flex;
    padding: 32px;
    position: relative;

    .video-section {
      flex-grow: 1;
      background: #f7f8fa;
      border-radius: 8px;
      overflow: hidden;
    }

    @media screen and (max-width: 1440px) {
      display: block;
    }
  }

  .video-content-toggle {
    .medium-font(16);
    color: #236cff;
    cursor: pointer;
    &.hide-toggle {
      position: absolute;
      bottom: 24px;
      right: 34px;
      & > .toggle-arrow {
        display: inline-block;
        transform: scale(1.5) translateX(4px) rotateZ(90deg);
      }
    }

    &.show-toggle {
      position: absolute;
      position: absolute;
      top: 32px;
      right: 0px;
      & > .toggle-arrow {
        display: inline-block;
        transform: scale(1.5) translateX(-4px) rotateZ(-90deg);
      }
    }
  }

  .video-split-footer,
  .video-split-header {
    display: none;
    position: relative;
  }

  .video-split-header {
    height: 32px;
  }

  .video-split-footer {
    height: 74px;
  }

  @media screen and (max-width: 1440px) {
    .video-split-footer,
    .video-split-header {
      display: block;
    }
  }

  .video-split-content {
    flex-shrink: 0;
    width: 321px;

    &.video-split-open {
      transform: translate3d(0, 0, 0);
    }

    @media screen and (max-width: 1440px) {
      position: absolute;
      z-index: 3;
      right: 0;
      top: 0;
      height: 100%;
      transform: translate3d(100%, 0, 0);

      width: 321px;
      background: #ffffff;
      box-shadow: 10px 2px 30px 0px rgba(112, 141, 199, 0.16);
      border-radius: 16px;
      border: 1px solid #edeff2;

      transition: transform 0.3s ease-in;
    }
  }
  .upload-btn {
    padding: 8px 16px;
    height: 40px;
    & > .icon-upload {
      margin-right: 4px;
    }
    &.ant-btn-default {
      .medium-font(16);
      .default-btn-mixins();
      color: #236CFF;
    }
    margin-bottom: 28px;
  }
  .remove-btn {
    display: inline-block;
    vertical-align: middle;
    width: 16px;
    height: 16px;
    background: url(~@/assets/remove.svg) left top / cover no-repeat;
    &:hover {
      cursor: pointer;
      background-image: url(~@/assets/remove-hover.svg);
    }
  }
  .download-icon {
    font-size: 18px;
    vertical-align: middle;

    margin-right: 17px;
    color: #181818;
    &:hover {
      cursor: pointer;
      color: #fea9a9;
    }
  }

  .coc-attach-table {
    ::v-deep {
      .ant-table-header {
        background: transparent;
      }
      .ant-table-thead > tr > th, .ant-table-tbody > tr > td {
        border-bottom-color: #EDEFF2;
        &:first-child {
          border-bottom-color: transparent;
        }
        padding: 12px 12px 12px 0;
      }
      .ant-table-thead > tr > th {
        background: transparent;
      }
      .ant-table-tbody {
        & > tr {
          & > td {
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            transition: none;
            color: #999B9F;
          }
          &:hover > td {
            background: #F3F6FB;
          }
        }
      }
    }
    .attach-name-td {
      color: #000;
      height: 32px;
      line-height: 32px;

       & > .file-entity-icon {
        width: 30px;
        height: 32px;
        vertical-align: top;
        margin-right: 32px;
      }
    }
  }
}

</style>
